import { Link } from 'react-router-dom';
import { Target, Users, Award, Heart, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const values = [
  {
    icon: Target,
    title: 'Career-Focused',
    description: 'Every program is designed with your career advancement in mind. We prepare you for real jobs, not just certifications.'
  },
  {
    icon: Users,
    title: 'Expert Instructors',
    description: 'Learn from certified ERP professionals with years of implementation and consulting experience.'
  },
  {
    icon: Award,
    title: 'Quality Training',
    description: 'Our curriculum is aligned with official certification exams and industry best practices.'
  },
  {
    icon: Heart,
    title: 'Student Success',
    description: 'We measure our success by your success. Your career growth is our top priority.'
  },
];

const milestones = [
  { year: '2019', event: 'Founded with a mission to democratize ERP training' },
  { year: '2020', event: 'Launched first online programs during pandemic' },
  { year: '2021', event: 'Expanded to cover 5 major ERP platforms' },
  { year: '2022', event: 'Reached 2,000 trained professionals' },
  { year: '2023', event: 'Partnered with 50+ corporate clients' },
  { year: '2024', event: 'Achieved 95% certification pass rate' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-50 via-white to-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div
              
              
            >
              <span className="inline-block px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-6">
                About LearnQuanta
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-brand-900 mb-6">
                Empowering Careers Through ERP Excellence
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                LearnQuanta is a specialized ERP training provider focused on delivering 
                high-quality, instructor-led programs that prepare professionals for successful 
                careers in enterprise software.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/programs">
                  <Button className="btn-primary" data-testid="explore-programs-btn">
                    Explore Programs
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="btn-secondary">
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </div>
            
            <div
              
              
              
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758518727707-b023e285b709?w=800&h=600&fit=crop" 
                  alt="LearnQuanta team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-brand-700">
        <div className="container-main">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '5,000+', label: 'Professionals Trained' },
              { number: '5', label: 'ERP Platforms' },
              { number: '95%', label: 'Certification Pass Rate' },
              { number: '50+', label: 'Corporate Clients' },
            ].map((stat, index) => (
              <div
                key={stat.label}
                
                
                
                
                className="text-center"
              >
                <p className="text-3xl lg:text-4xl font-bold text-white mb-1">{stat.number}</p>
                <p className="text-brand-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-28">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-heading mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  LearnQuanta was founded with a simple mission: make high-quality ERP training 
                  accessible to professionals worldwide. We saw a gap in the market for practical, 
                  career-focused training that goes beyond theory.
                </p>
                <p>
                  Our founders, experienced ERP consultants themselves, understood the challenges 
                  faced by professionals trying to break into or advance in the ERP industry. 
                  Traditional training was often expensive, impractical, or disconnected from 
                  real-world requirements.
                </p>
                <p>
                  Today, we deliver instructor-led online training that combines the best of 
                  classroom learning with the flexibility of remote education. Our programs are 
                  designed by industry experts and taught by certified professionals who bring 
                  real implementation experience to every session.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?w=600&h=500&fit=crop" 
                alt="Professional team"
                className="rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 lg:py-28 bg-brand-50/50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Our Values</h2>
            <p className="section-subheading mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                
                
                
                
              >
                <Card className="program-card h-full">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 bg-brand-100 rounded-xl flex items-center justify-center mb-4">
                      <value.icon className="w-7 h-7 text-brand-700" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-brand-900 mb-2">{value.title}</h3>
                    <p className="text-slate-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Philosophy */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <h2 className="section-heading mb-8 text-center">Our Training Philosophy</h2>
            <div className="space-y-6">
              {[
                { title: 'Learn by Doing', desc: 'Theory is important, but practice makes perfect. Our programs emphasize hands-on learning with real ERP systems.' },
                { title: 'Career-First Approach', desc: "We don't just teach concepts - we prepare you for the job market with practical skills employers actually want." },
                { title: 'Expert Guidance', desc: 'Our instructors are certified professionals with real implementation experience, not just trainers reading from slides.' },
                { title: 'Continuous Support', desc: 'Learning doesn\'t stop when class ends. We provide ongoing support through community access and career guidance.' },
              ].map((item, index) => (
                <div
                  key={item.title}
                  
                  
                  
                  
                  className="flex items-start space-x-4"
                >
                  <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-brand-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-brand-900 text-lg">{item.title}</h3>
                    <p className="text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 lg:py-28 bg-brand-50/50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Our Journey</h2>
            <p className="section-subheading mx-auto">Key milestones in our growth</p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-brand-200" />
              {milestones.map((milestone, index) => (
                <div
                  key={milestone.year}
                  
                  
                  
                  
                  className="relative pl-20 pb-8 last:pb-0"
                >
                  <div className="absolute left-4 w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full" />
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <span className="text-brand-600 font-mono font-bold">{milestone.year}</span>
                    <p className="text-slate-700">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-brand-700">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Join Our Community?
            </h2>
            <p className="text-lg text-brand-100 mb-8">
              Start your ERP career journey with LearnQuanta. 
              Explore our programs or talk to an advisor today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/programs">
                <Button className="bg-white text-brand-700 hover:bg-brand-50 rounded-full px-8 py-3 font-medium">
                  View Programs
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-medium">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
